import { derivedContext } from '@/contexts/context.derived';
import { optionContext } from '@/contexts/context.option';
import { productContext } from '@/contexts/context.product';
import { stateContext } from '@/contexts/context.state';
// Legacy per-context renders remain available until full migration is complete
import { CollectChildEvent, CollectChildRemovedEvent } from '@/events/events';
import { renderAttributes } from '@/renders/attributes';
import { OBS_ATTRS_OPTION_PLUS_STATE } from '@/renders/observe';
import { Context, ContextEvent, ContextType } from '@lit/context';
import type { ProductOption } from '@repobit/dex-store';

type AnyContext = Context<unknown, unknown>;
type CtxVal<C extends AnyContext> = ContextType<C>;
type ContextMap = Record<string, AnyContext>;
type ContextValues<M extends ContextMap> = { [K in keyof M]: CtxVal<M[K]> };
type BindContext<M extends ContextMap> = {
  el           : HTMLElement,
  contexts     : M,
  logic        : (values: ContextValues<M>) => void | Promise<void>,
  observedAttrs: string[],
  requireAll?  : boolean
}
export type RenderNode = HTMLElement & { updateComplete?: Promise<void> };
export const DATA_RENDER_NODE = "[data-store-render]";
export const DATA_HIDE_NODE = "[data-store-hide]";

// Track initialized render nodes to avoid duplicate bindings
const RENDER_BOUND = new WeakSet<RenderNode>();
const RENDER_DISPOSE = new WeakMap<RenderNode, () => void>();
const RENDER_RESOLVE = new WeakMap<RenderNode, () => void>();
const RENDER_ORIGINAL_REMOVE = new WeakMap<RenderNode, () => void>();

export const attachContext = <C extends AnyContext>(
  el: HTMLElement,
  context: C,
  onValue: (value: ContextType<C>) => void,
  { subscribe = true }: { subscribe?: boolean } = {}
) => {
  let dispose: (() => void) | undefined;

  el.dispatchEvent(
    new ContextEvent(
      context,
      el,
      (value: ContextType<C>, d?: () => void) => {
        onValue(value);
        dispose = d; // may be provided when subscribe === true
      },
      subscribe
    )
  );

  // call this later to unsubscribe (if provider gave you a disposer)
  return () => dispose?.();
}

export const observeAttributes = (
  el: HTMLElement,
  onChange: () => void,
  filter?: string[]
) => {
  const mo = new MutationObserver((mutations) => {
    if (mutations.length === 0) return;
    onChange();
  });

  mo.observe(el, {
    attributes     : true,
    attributeFilter: filter // e.g. ['user-id','subscribe']
  });

  // return disposer
  return () => mo.disconnect();
}

/**
 * Subscribes to all contexts in `contexts`, coalesces updates in a microtask,
 * and calls `logic` with the latest values once ALL are present.
 * Returns a single disposer.
 */
export function bindContext<M extends ContextMap>({
  el,
  contexts,
  logic,
  observedAttrs = [],
  requireAll = true
}: BindContext<M>) {
  const keys = Object.keys(contexts) as (keyof M)[];
  const latest: Partial<ContextValues<M>> = {};
  const seen = new Set<keyof M>();

  let scheduled = false; // a microtask has been queued
  let running   = false; // logic is currently executing
  let needsRun  = false; // something changed; run again after current finishes
  let disposed  = false;

  const haveAll = () => seen.size === keys.length;
  const haveAny = () => seen.size > 0;

  const pump = async () => {
    scheduled = false;

    if (disposed) return;

    // If we're already running, just mark that we need another pass.
    if (running) {
      needsRun = true;
      return;
    }

    // Run when required contexts are present at least once.
    if (requireAll ? !haveAll() : !haveAny()) return;

    running = true;
    try {
      do {
        needsRun = false;

        // Snapshot so each run sees a stable view even if `latest` changes while awaiting.
        const snapshot = {} as ContextValues<M>;
        for (const k of keys) (snapshot[k] as unknown) = latest[k];

        await Promise.resolve(logic(snapshot));
      } while (needsRun && !disposed); // drain one more time if updates queued while running
    } catch (err) {
      // Keep the queue alive even if user logic throws.
      console.error(err);
    } finally {
      running = false;
      if (needsRun && !disposed) {
        scheduled = true;
        queueMicrotask(pump);
      }
    }
  };

  const scheduleRun = () => {
    if (disposed) return;
    needsRun = true;
    if (!scheduled) {
      scheduled = true;
      queueMicrotask(pump);
    }
  };

  // subscribe to each requested context
  const disposers = keys.map((k) =>
    attachContext(el, contexts[k], (value) => {
      latest[k] = value;
      seen.add(k);
      scheduleRun();
    })
  );

  // optional: observe attributes once for the whole group
  const stopAttrs =
    observedAttrs.length > 0
      ? observeAttributes(el, scheduleRun, observedAttrs)
      : () => {};

  // single disposer
  return () => {
    disposed = true;
    stopAttrs();
    seen.clear();
    for (const d of disposers) d();
  };
}

const addLogic = (el: RenderNode, rendered: (value: void | PromiseLike<void>) => void) => {
  // Single binder aggregates both contexts and renders when either changes
  return bindContext({
    el,
    contexts: { optionContext, stateContext, productContext, derivedContext },
    logic   : async ({ optionContext, stateContext, productContext, derivedContext }) => {
      const opt = await optionContext as ProductOption | null | undefined;
      await renderAttributes(
        el,
        {
          option : opt,
          state  : stateContext,
          product: productContext,
          derived: derivedContext
        }
      );
      rendered();
    },
    observedAttrs: [...OBS_ATTRS_OPTION_PLUS_STATE]
  });
};

const teardownRenderNode = (el: RenderNode) => {
  const hadState =
    RENDER_BOUND.has(el)
    || RENDER_DISPOSE.has(el)
    || RENDER_RESOLVE.has(el)
    || RENDER_ORIGINAL_REMOVE.has(el);
  if (!hadState) return;

  const dispose = RENDER_DISPOSE.get(el);
  try { dispose?.(); } catch { /* ignore */ }

  // Resolve any outstanding updateComplete so parents don't hang
  const resolve = RENDER_RESOLVE.get(el);
  try { resolve?.(); } catch { /* ignore */ }

  const originalRemove = RENDER_ORIGINAL_REMOVE.get(el);
  if (originalRemove) {
    el.remove = originalRemove;
  }

  try { el.dispatchEvent(new CollectChildRemovedEvent({ child: el })); } catch { /* ignore */ }
  RENDER_DISPOSE.delete(el);
  RENDER_BOUND.delete(el);
  RENDER_RESOLVE.delete(el);
  RENDER_ORIGINAL_REMOVE.delete(el);
};

export const handleRenderNode = (el: RenderNode) => {
  // Idempotent: don't re-bind the same node
  if (RENDER_BOUND.has(el)) return;
  RENDER_BOUND.add(el);

  const { promise, resolve } = Promise.withResolvers<void>();
  el.updateComplete = promise;
  const dispose = addLogic(el, resolve);
  RENDER_DISPOSE.set(el, dispose);
  RENDER_RESOLVE.set(el, resolve);

  // Register as contextual child so nearest StateNode parents can await it
  try { el.dispatchEvent(new CollectChildEvent({ child: el })); } catch { /* ignore */ }

  const originalRemove = el.remove;
  RENDER_ORIGINAL_REMOVE.set(el, originalRemove);

  el.remove = () => {
    teardownRenderNode(el);
    originalRemove.call(el);
  }
};

export const cleanupRenderNode = (el: RenderNode) => {
  teardownRenderNode(el);
};
